import { inCommands } from '../../concepts/commands/CommandRegistry';
import { Communicator } from '../../concepts/objects';
import { Point } from '../../platform/geom/Point';
import { ActiveObject, Connector, Package, PackageHost } from '../Container';
import { AdapterInfo } from './AdapterInfo';
import { BoardInfo } from './BoardInfo';
import { Camera } from './Camera';
import { CameraMechanics } from './CameraMechanics';
import { ChaosMechanics } from './ChaosMechanics';
import { inClipboardCommands } from './commands/ClipboardCommands';
import { inOrderCommands } from './commands/OrderCommands';
import { inOrganizeCommands } from './commands/OrganizeCommands';
import { inSelectionCommands } from './commands/SelectionCommands';
import { inTransformCommands } from './commands/TransformCommands';
import { ConfettiCannon } from './ConfettiCannon';
import { coreAssetsLoader } from './CoreAssets';
import { giveFeedback, hoist, setBoardName } from './CoreCommands';
import { inDeveloperCommands } from './developer/DeveloperCommands';
import { IsDeveloperMode } from './developer/IsDeveloperMode';
import { EditorModeInfo } from './EditorModeInfo';
import { IsEditorPaletteOpen } from './EditorPalette';
import { EditorSession } from './EditorSession';
import { DuplicateInfo } from './entities/DuplicateInfo';
import { DuplicateMechanics } from './entities/DuplicateMechanics';
import { PhysicalMechanics } from './entities/PhysicalMechanics';
import { StateProperty } from './ExtendableStore';
import { CommandInterpreter } from './extensibility/CommandInterpreter';
import { GridMechanics } from './GridMechanics';
import { GridSystem } from './GridSystem';
import { ContentInjector, inStandardContentProcessors } from './injection/ContentInjector';
import { InteractionHelper } from './InteractionHelper';
import { MetadataProvider } from './MetadataProvider';
import { MyProfileInfo } from './MyProfileInfo';
import { MyRoleInfoProperty } from './MyRoleInfo';
import { NetController } from './nets/NetController';
import { NetOverlay } from './nets/NetOverlay';
import { NetRegister } from './nets/NetRegister';
import { OverlayComposition } from './overlay/OverlayComposition';
import { OverlayManager } from './overlay/OverlayManager';
import { JoinMechanics } from './participants/JoinMechanics';
import { ParticipantManager } from './participants/ParticipantManager';
import { ParticipantMechanics } from './participants/ParticipantMechanics';
import { ParticipantStripInfo } from './participants/ParticipantStripInfo';
import { PointerInfo } from './PointerInfo';
import { PropertyHistory } from './properties/PropertyHistory';
import { PropertyStripInfo } from './properties/PropertyStripInfo';
import { PropertyStripMechanics } from './properties/PropertyStripMechanics';
import { SizeWidgetMechanics } from './properties/SizeWidgetMechanics';
import { RootOverride } from './RootOverride';
import { Selection, SelectionSet } from './Selection';
import { Telemetry } from './Telemetry';
import { cloneTool } from './tooling/CloneTool';
import { inTool } from './tooling/ToolManager';
import { ToolOverlay } from './tooling/ToolOverlay';
import { SpatialIndex } from './utilities/SpatialIndex';
import { EffectDirector } from './visualization/EffectDirector';
import { GraffitiMechanics } from './visualization/GraffitiMechanics';
import { PoseMechanics } from './visualization/PoseMechanics';
import { VolumeMechanics } from './VolumeMechanics';
import { ActiveMeetingInfo } from './workflow/ActiveMeetingInfo';
import { switchToDesignMode } from './workflow/DesignModeCommands';
import { DesignModeMechanics } from './workflow/DesignModeMechanics';
import { EditorModeMechanics } from './workflow/EditorModeMechanics';
import { IsElevated } from './workflow/IsElevated';
import { inMeetingCommands } from './workflow/MeetingCommands';
import { MeetingMechanics } from './workflow/MeetingMechanics';
import { switchToMeetingMode } from './workflow/MeetingModeCommands';
import { MeetingModeMechanics } from './workflow/MeetingModeMechanics';
import { ViewMode } from './workflow/ViewMode';
import { ViewModeMechanics } from './workflow/ViewModeMechanics';
import { IsDropping } from './workspace/DropSupport';
import { PawnManager } from './workspace/participants/PawnManager';
import { PawnMechanics } from './workspace/participants/PawnMechanics';
import { PresenceMechanics } from './workspace/participants/PresenceMechanics';
import { ShittyClientRelay } from './workspace/participants/ShittyClientRelay';
import { SuggestedWidgetScaleProperty } from './workspace/SuggestedWidgetScaleProperty';
import { WidgetManager } from './workspace/widgets/WidgetManager';

const inDataProperties: Connector = async r => {
    const comms = r.call(Communicator);
    const dataProvider = r.call(MetadataProvider);
    const profileData = await dataProvider.fetchProfileData();
    const boardData = await dataProvider.fetchBoardData();
    const session = r.call(EditorSession);
    session.extend(StateProperty.const(MyProfileInfo, profileData));
    session.extend(StateProperty.variable(BoardInfo, boardData));
    return [
        comms.recv('boardName', (client, data) => {
            session.patch(BoardInfo, data);
        }),
    ];
};

const inOtherSessionProperties: Connector = r => {
    const session = r.call(EditorSession);
    // Variables:
    session.extend(StateProperty.variable(ActiveMeetingInfo, ActiveMeetingInfo.empty));
    session.extend(StateProperty.variable(AdapterInfo, AdapterInfo.empty));
    session.extend(StateProperty.variable(DuplicateInfo, DuplicateInfo.empty));
    session.extend(StateProperty.variable(EditorModeInfo, ViewMode));
    session.extend(StateProperty.variable(IsDeveloperMode, IsDeveloperMode.empty));
    session.extend(StateProperty.variable(IsDropping, false));
    session.extend(StateProperty.variable(IsEditorPaletteOpen, false));
    session.extend(StateProperty.variable(IsElevated, IsElevated.empty));
    session.extend(StateProperty.variable(ParticipantStripInfo, ParticipantStripInfo.empty));
    session.extend(StateProperty.variable(PointerInfo, { position: Point.zero, targets: [] }));
    session.extend(StateProperty.variable(PropertyStripInfo, PropertyStripInfo.empty));
    session.extend(StateProperty.variable(RootOverride, RootOverride.empty));
    session.extend(StateProperty.variable(Selection, SelectionSet.empty));
    // Custom:
    session.extend(r.call(SuggestedWidgetScaleProperty));
    session.extend(r.call(MyRoleInfoProperty));
};

export class CorePackage implements Package {

    public install = (host: PackageHost) => host
        .load(coreAssetsLoader)
        .register(Camera, 'singleton')
        .register(CameraMechanics, 'singleton', 'startable')
        .register(ChaosMechanics, 'singleton', 'startable')
        .register(CommandInterpreter, 'singleton')
        .register(ConfettiCannon, 'singleton')
        .register(ContentInjector, 'singleton')
        .register(DesignModeMechanics, 'singleton', 'startable')
        .register(EditorModeMechanics, 'singleton', 'startable')
        .register(DuplicateMechanics, 'singleton', 'startable')
        .register(EditorSession, 'singleton')
        .register(EffectDirector, 'singleton', 'startable')
        .register(GraffitiMechanics, 'singleton', 'startable')
        .register(GridMechanics, 'singleton', 'startable')
        .register(VolumeMechanics, 'singleton', 'startable')
        .register(GridSystem, 'singleton')
        .register(InteractionHelper, 'singleton')
        .register(JoinMechanics, 'singleton', 'startable')
        .register(MeetingMechanics, 'singleton', 'startable')
        .register(MeetingModeMechanics, 'singleton', 'startable')
        .register(MyRoleInfoProperty, 'singleton')
        .register(NetController, 'singleton', 'startable')
        .register(NetOverlay, 'singleton')
        .register(NetRegister, 'singleton')
        .register(OverlayManager, 'singleton')
        .register(OverlayComposition, 'singleton')
        .register(ParticipantManager, 'singleton', 'startable')
        .register(ParticipantMechanics, 'singleton', 'startable')
        .register(PawnManager, 'singleton')
        .register(PawnMechanics, 'singleton', 'startable')
        .register(PhysicalMechanics, 'singleton', 'startable')
        .register(PoseMechanics, 'singleton', 'startable')
        .register(PresenceMechanics, 'singleton', 'startable')
        .register(PropertyHistory, 'singleton')
        .register(PropertyStripMechanics, 'singleton', 'startable')
        .register(SizeWidgetMechanics, 'singleton', 'startable')
        .register(ShittyClientRelay, 'singleton')
        .register(SpatialIndex, 'singleton', 'startable')
        .register(SuggestedWidgetScaleProperty, 'singleton', 'startable')
        .register(Telemetry, 'singleton', 'startable')
        .register(ToolOverlay, 'singleton', 'startable')
        .register(ViewModeMechanics, 'singleton', 'startable')
        .register(WidgetManager, 'singleton')
        .plug(inDataProperties, inOtherSessionProperties)
        .plug(inStandardContentProcessors)
        .plug(
            inCommands(setBoardName, giveFeedback, hoist, switchToDesignMode, switchToMeetingMode),
            inClipboardCommands(),
            inDeveloperCommands(),
            inMeetingCommands(),
            inOrderCommands(),
            inOrganizeCommands(),
            inSelectionCommands(),
            inTransformCommands(),
            inTool(cloneTool),
        )
        .register(Debug, 'singleton', 'startable');
}

class Debug extends ActiveObject {

    public start() {
    }
}
